import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import appointmentService from '../services/appointmentService';
import AppointmentModal from '../components/AppointmentModal';

const HOURS = Array.from({ length: 11 }, (_, i) => i + 8); // 8:00 – 18:00

const TYPE_LABELS   = { firma: 'Firma', consulta: 'Consulta', entrega: 'Entrega', seguimiento: 'Seguimiento', interno: 'Interno' };
const STATUS_COLORS = { programada: '#3b82f6', confirmada: '#10b981', realizada: '#6b7280', cancelada: '#ef4444', reprogramada: '#f59e0b' };
const STATUS_LABELS = { programada: 'Programada', confirmada: 'Confirmada', realizada: 'Realizada', cancelada: 'Cancelada', reprogramada: 'Reprogramada' };

function getWeekDays(ref) {
  const d = new Date(ref), day = d.getDay();
  const mon = new Date(d); mon.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  return Array.from({ length: 7 }, (_, i) => { const x = new Date(mon); x.setDate(mon.getDate() + i); return x; });
}
function fmtDate(d)  { return d.toISOString().split('T')[0]; }
function sameDay(a, b){ return fmtDate(a) === fmtDate(b); }
function fmtTime(dt) { return new Date(dt).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }); }

const btnSt = { padding: '7px 10px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, color: '#374151', backgroundColor: '#fff', outline: 'none', cursor: 'pointer' };

export default function AgendaCalendar() {
  const navigate = useNavigate();
  const [view, setView]           = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [preselectedDate, setPreselectedDate] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [statsHover, setStatsHover] = useState(false);

  const weekDays = getWeekDays(currentDate);
  const dateFrom = fmtDate(weekDays[0]);
  const dateTo   = fmtDate(weekDays[weekDays.length - 1]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (filterStatus) params.status = filterStatus;
      const res = await appointmentService.getAll(params);
      setAppointments(res.data.data ?? res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const goToday = () => setCurrentDate(new Date());
  const goPrev  = () => { const d = new Date(currentDate); d.setDate(d.getDate() - (view === 'day' ? 1 : 7)); setCurrentDate(d); };
  const goNext  = () => { const d = new Date(currentDate); d.setDate(d.getDate() + (view === 'day' ? 1 : 7)); setCurrentDate(d); };

  const openCreate = (date = null) => { setEditingAppointment(null); setPreselectedDate(date); setShowModal(true); };

  const getForDay  = (d)       => appointments.filter(a => sameDay(new Date(a.start_datetime), d));
  const getForHour = (d, hour) => appointments.filter(a => { const s = new Date(a.start_datetime); return sameDay(s, d) && s.getUTCHours() === hour; });

  // Citas fuera del rango visible (antes de las 8 o después de las 18)
  const outOfRangeAppts = appointments.filter(a => {
    const h = new Date(a.start_datetime).getUTCHours();
    return h < 8 || h >= 18;
  });

  const monthLabel = currentDate.toLocaleString('es-MX', { month: 'long', year: 'numeric' });
  const isToday = (d) => sameDay(d, new Date());

  const ApptChip = ({ a, compact = false }) => (
    <div
      onClick={e => { e.stopPropagation(); navigate(`/agenda/${a.id}`); }}
      style={{
        background: STATUS_COLORS[a.status], color: '#fff',
        borderRadius: compact ? 4 : 6, padding: compact ? '2px 5px' : '4px 8px',
        marginBottom: 2, fontSize: compact ? 11 : 12, cursor: 'pointer',
        overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
        transition: 'opacity 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
      onMouseLeave={e => e.currentTarget.style.opacity = '1'}
      title={`${a.title} — ${fmtTime(a.start_datetime)}–${fmtTime(a.end_datetime)} · ${a.user?.name}`}>
      {fmtTime(a.start_datetime)} {a.title}
    </div>
  );

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>

        {/* Navegación de fechas */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h4 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827', letterSpacing: '-0.3px', textTransform: 'capitalize' }}>{monthLabel}</h4>
          <button onClick={goPrev}   style={{ ...btnSt, padding: '5px 10px', fontSize: 14 }}>‹</button>
          <button onClick={goToday}  style={{ ...btnSt, padding: '5px 12px', fontWeight: 600, color: '#4F63D2', borderColor: '#4F63D2', backgroundColor: '#EEF0FB' }}>Hoy</button>
          <button onClick={goNext}   style={{ ...btnSt, padding: '5px 10px', fontSize: 14 }}>›</button>
        </div>

        {/* Controles: filtro + vistas + acciones */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>

          {/* Filtro estado — pill activo */}
          <div style={{ position: 'relative' }}>
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              style={{
                ...btnSt,
                minWidth: 140,
                borderColor: filterStatus ? '#4F63D2' : '#E5E7EB',
                backgroundColor: filterStatus ? '#EEF0FB' : '#fff',
                color: filterStatus ? '#4F63D2' : '#374151',
                fontWeight: filterStatus ? 700 : 400,
                paddingRight: filterStatus ? 28 : 10,
              }}>
              <option value="">Todos los estados</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {/* Punto indicador cuando hay filtro activo */}
            {filterStatus && (
              <span
                onClick={() => setFilterStatus('')}
                title="Quitar filtro"
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, borderRadius: '50%', background: '#4F63D2', color: '#fff', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontWeight: 700 }}>
                ✕
              </span>
            )}
          </div>

          {/* Toggle vistas */}
          <div style={{ display: 'flex', border: '1.5px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
            {[['week','Semana'],['day','Día'],['list','Lista']].map(([v, label]) => (
              <button key={v} onClick={() => setView(v)}
                style={{ padding: '6px 13px', border: 'none', backgroundColor: view === v ? '#4F63D2' : '#fff', color: view === v ? '#fff' : '#374151', fontWeight: view === v ? 700 : 500, fontSize: 13, cursor: 'pointer', borderRight: v !== 'list' ? '1px solid #E5E7EB' : 'none', transition: 'all 0.15s' }}>
                {label}
              </button>
            ))}
          </div>

          {/* Nueva cita */}
          <button onClick={() => openCreate()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 13, border: 'none', cursor: 'pointer', transition: 'background 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#3d4fb8'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#4F63D2'}>
            <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
            Nueva cita
          </button>

          {/* Estadísticas — con hover */}
          <button
            onClick={() => navigate('/agenda/stats')}
            onMouseEnter={() => setStatsHover(true)}
            onMouseLeave={() => setStatsHover(false)}
            style={{ padding: '7px 12px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: statsHover ? '#F9FAFB' : '#fff', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'background 0.15s' }}>
            📊 Estadísticas
          </button>
        </div>
      </div>

      {/* ── Leyenda de colores — integrada en el header ── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {Object.entries(STATUS_LABELS).map(([k, v]) => (
          <button
            key={k}
            onClick={() => setFilterStatus(filterStatus === k ? '' : k)}
            title={`Filtrar: ${v}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 11, color: filterStatus === k ? STATUS_COLORS[k] : '#6B7280',
              fontWeight: filterStatus === k ? 700 : 400,
              background: filterStatus === k ? STATUS_COLORS[k] + '18' : 'none',
              border: `1px solid ${filterStatus === k ? STATUS_COLORS[k] + '44' : 'transparent'}`,
              borderRadius: 20, padding: '3px 8px', cursor: 'pointer',
              transition: 'all 0.15s',
            }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: STATUS_COLORS[k], display: 'inline-block', flexShrink: 0 }} />
            {v}
          </button>
        ))}
        {filterStatus && (
          <button onClick={() => setFilterStatus('')}
            style={{ fontSize: 11, color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: '3px 4px' }}>
            Quitar filtro
          </button>
        )}
      </div>

      {/* ── Banner citas fuera de rango ── */}
      {!loading && outOfRangeAppts.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderRadius: 8, backgroundColor: '#FEF3C7', border: '1px solid #FDE68A', marginBottom: 12, fontSize: 12, color: '#92400E' }}>
          <span>⚠️</span>
          <span>
            <strong>{outOfRangeAppts.length} cita{outOfRangeAppts.length > 1 ? 's' : ''}</strong> fuera del rango visible (8:00–18:00):
            {' '}{outOfRangeAppts.map(a => `${a.title} (${fmtTime(a.start_datetime)})`).join(', ')}
          </span>
        </div>
      )}

      {/* ── Vista Semana ── */}
      {view === 'week' && (
        <div style={{ position: 'relative', background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'auto' }}>
          {/* Overlay de loading */}
          {loading && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.75)', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 10 }}>
              <div style={{ width: 28, height: 28, border: '3px solid #EEF0FB', borderTopColor: '#4F63D2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
            </div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
            <thead>
              <tr>
                <th style={{ width: 52, padding: '10px 8px', borderBottom: '2px solid #E5E7EB', backgroundColor: '#F9FAFB' }} />
                {weekDays.map((d, i) => {
                  const dayCount = getForDay(d).length;
                  return (
                    <th key={i}
                      onClick={() => { setCurrentDate(d); setView('day'); }}
                      style={{ padding: '10px 6px', textAlign: 'center', borderBottom: '2px solid #E5E7EB', cursor: 'pointer', backgroundColor: isToday(d) ? '#EEF0FB' : '#F9FAFB', transition: 'background 0.15s' }}
                      onMouseEnter={e => !isToday(d) && (e.currentTarget.style.backgroundColor = '#F3F4F6')}
                      onMouseLeave={e => !isToday(d) && (e.currentTarget.style.backgroundColor = '#F9FAFB')}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: isToday(d) ? '#4F63D2' : '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>
                        {d.toLocaleString('es-MX', { weekday: 'short' })}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: isToday(d) ? '#4F63D2' : '#111827', width: 32, height: 32, borderRadius: '50%', backgroundColor: isToday(d) ? '#EEF0FB' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                          {d.getDate()}
                        </div>
                        {/* Badge de conteo de citas por día */}
                        {dayCount > 0 && (
                          <span style={{ fontSize: 9, fontWeight: 800, padding: '1px 5px', borderRadius: 20, backgroundColor: isToday(d) ? '#4F63D2' : '#1e2a4a', color: '#fff', minWidth: 16, textAlign: 'center' }}>
                            {dayCount}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {HOURS.map(hour => (
                <tr key={hour} style={{ height: 60 }}>
                  <td style={{ padding: '4px 8px', fontSize: 11, color: '#9CA3AF', textAlign: 'right', verticalAlign: 'top', borderBottom: '1px solid #F3F4F6', whiteSpace: 'nowrap', fontWeight: 500 }}>
                    {hour}:00
                  </td>
                  {weekDays.map((d, di) => {
                    const appts = getForHour(d, hour);
                    return (
                      <td key={di}
                        onClick={() => openCreate(new Date(new Date(d).setHours(hour, 0, 0, 0)))}
                        style={{ padding: 3, verticalAlign: 'top', borderBottom: '1px solid #F3F4F6', borderLeft: '1px solid #F9FAFB', cursor: 'pointer', backgroundColor: isToday(d) ? '#FAFBFF' : '#fff' }}
                        onMouseEnter={e => !appts.length && (e.currentTarget.style.backgroundColor = '#F9FAFB')}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = isToday(d) ? '#FAFBFF' : '#fff'}>
                        {appts.map(a => <ApptChip key={a.id} a={a} compact />)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Vista Día ── */}
      {view === 'day' && (
        <div style={{ position: 'relative', background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          {loading && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.75)', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 10 }}>
              <div style={{ width: 28, height: 28, border: '3px solid #EEF0FB', borderTopColor: '#4F63D2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
            </div>
          )}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: '#111827', textTransform: 'capitalize' }}>
              {currentDate.toLocaleString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
            {(() => { const c = getForDay(currentDate).length; return c > 0 ? <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, backgroundColor: '#EEF0FB', color: '#4F63D2' }}>{c} cita{c > 1 ? 's' : ''}</span> : null; })()}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {HOURS.map(hour => {
                const appts = getForHour(currentDate, hour);
                return (
                  <tr key={hour} style={{ height: 72 }}>
                    <td style={{ width: 64, padding: '6px 12px', fontSize: 11, color: '#9CA3AF', verticalAlign: 'top', borderBottom: '1px solid #F3F4F6', textAlign: 'right', fontWeight: 500, whiteSpace: 'nowrap' }}>
                      {hour}:00
                    </td>
                    <td style={{ padding: 6, verticalAlign: 'top', borderBottom: '1px solid #F3F4F6', cursor: 'pointer' }}
                      onClick={() => openCreate(new Date(new Date(currentDate).setHours(hour, 0, 0, 0)))}>
                      {appts.map(a => (
                        <div key={a.id}
                          onClick={e => { e.stopPropagation(); navigate(`/agenda/${a.id}`); }}
                          style={{ background: STATUS_COLORS[a.status], color: '#fff', borderRadius: 7, padding: '5px 10px', marginBottom: 4, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'opacity 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                          onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                          <div>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>{a.title}</span>
                            <span style={{ fontSize: 11, marginLeft: 8, opacity: 0.8 }}>{TYPE_LABELS[a.type]}</span>
                          </div>
                          <div style={{ fontSize: 11, textAlign: 'right', opacity: 0.85 }}>
                            <div>{a.user?.name}</div>
                            <div>{fmtTime(a.start_datetime)} — {fmtTime(a.end_datetime)}</div>
                          </div>
                        </div>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Vista Lista — muestra todos los días incluso los vacíos ── */}
      {view === 'list' && (
        <div style={{ position: 'relative' }}>
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 48 }}>
              <div style={{ width: 28, height: 28, border: '3px solid #EEF0FB', borderTopColor: '#4F63D2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
            </div>
          )}
          {!loading && appointments.length === 0 && (
            <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', padding: '48px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📅</div>
              <p style={{ fontSize: 15, fontWeight: 600, color: '#374151', margin: '0 0 4px' }}>No hay citas esta semana</p>
              <p style={{ fontSize: 13, color: '#9CA3AF', margin: 0 }}>Crea una nueva cita para comenzar</p>
            </div>
          )}
          {!loading && appointments.length > 0 && weekDays.map((d, di) => {
            const dayAppts = getForDay(d);
            return (
              <div key={di} style={{ marginBottom: 12 }}>
                {/* Encabezado del día — siempre visible */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderRadius: 8, backgroundColor: isToday(d) ? '#EEF0FB' : '#F9FAFB', border: `1px solid ${isToday(d) ? '#C7D2F8' : '#E5E7EB'}`, marginBottom: dayAppts.length ? 6 : 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: isToday(d) ? '#4F63D2' : '#374151', textTransform: 'capitalize', flex: 1 }}>
                    {d.toLocaleString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </span>
                  {isToday(d) && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, backgroundColor: '#4F63D2', color: '#fff' }}>Hoy</span>}
                  {dayAppts.length > 0
                    ? <span style={{ fontSize: 11, fontWeight: 700, color: isToday(d) ? '#4F63D2' : '#374151' }}>{dayAppts.length} cita{dayAppts.length > 1 ? 's' : ''}</span>
                    : <span style={{ fontSize: 11, color: '#D1D5DB', fontStyle: 'italic' }}>Sin citas</span>}
                </div>

                {/* Citas del día */}
                {dayAppts.map(a => (
                  <div key={a.id}
                    onClick={() => navigate(`/agenda/${a.id}`)}
                    style={{ background: '#fff', borderRadius: 9, border: '1px solid #E5E7EB', borderLeft: `4px solid ${STATUS_COLORS[a.status]}`, padding: '11px 14px', marginBottom: 6, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'background 0.15s', marginLeft: 4 }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FAFBFF'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
                    <div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{a.title}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 20, backgroundColor: STATUS_COLORS[a.status] + '22', color: STATUS_COLORS[a.status], marginLeft: 8 }}>{STATUS_LABELS[a.status]}</span>
                      <span style={{ fontSize: 12, color: '#9CA3AF', marginLeft: 6 }}>{TYPE_LABELS[a.type]}</span>
                      {a.client && <span style={{ fontSize: 12, color: '#9CA3AF', marginLeft: 6 }}>· {a.client.name}</span>}
                    </div>
                    <div style={{ fontSize: 12, color: '#9CA3AF', textAlign: 'right', flexShrink: 0 }}>
                      <div>{fmtTime(a.start_datetime)} — {fmtTime(a.end_datetime)}</div>
                      <div>{a.user?.name}</div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <AppointmentModal
          appointment={editingAppointment}
          preselectedDate={preselectedDate}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load(); }}
        />
      )}
    </div>
  );
}
